import { useState } from 'react';

import Slider from 'react-slick';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import { breakpoints } from 'Components/ThemeProvider';
import { Typography } from 'Components/Typography';

import { howProseWorksContent as content } from 'assets/content/landingPages/stylingGel';
import Icon1 from 'assets/images/landingPages/stylingGel/icon-1.svg';
import Icon2 from 'assets/images/landingPages/stylingGel/icon-2.svg';
import Icon3 from 'assets/images/landingPages/stylingGel/icon-3.svg';

import useMedia from 'utils/useMedia';
import useResponsiveVariant from 'utils/useResponsiveVariant';

import { SliderArrow } from './SliderArrowStylingGel';

const CardContainer = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 85%;
  margin: 0 auto;
  gap: ${legacyTheme.spacing.s24};
`;

const StyledTextSectionTitle = styled(Typography)`
  margin: 0;
  padding: 0 0 ${legacyTheme.spacing.s36} 0;
`;
const Title = styled(Typography)`
  ${legacyTheme.breakpoints.up('sm')} {
    padding-top: ${legacyTheme.spacing.s36};
  }
  ${legacyTheme.breakpoints.up('lg')} {
    padding-top: ${legacyTheme.spacing.s36};
  }
`;

const SectionContainer = styled.div`
  padding: ${legacyTheme.spacing.s40} ${legacyTheme.spacing.s40} ${legacyTheme.spacing.s96};

  width: 100vw;
  background-color: ${theme.colors.neutral[900]};
  color: ${theme.colors.neutral[100]};

  ${legacyTheme.breakpoints.up('lg')} {
    padding: ${legacyTheme.spacing.s144} ${legacyTheme.spacing.s40};
  }
`;

const StepCards = styled('div')`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-gap: ${legacyTheme.spacing.s48};
`;

const StepContainer = styled('div')`
  display: grid;
`;

const IconsContainer = styled('div')`
  display: grid;
  grid-gap: ${legacyTheme.spacing.s48};
`;
const LineContainer = styled('div')`
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  grid-row: 1 / -1;
  grid-column: 1 / -1;
`;
const Line = styled('div')`
  height: 1px;
  width: 70%;
  background: ${theme.colors.neutral[100]};
`;
const Icon = styled('img')`
  width: 60px;
`;

const IconContainer = styled('div')`
  display: grid;
  width: 100%;
  grid-template-columns: 1fr 1fr 1fr;
  grid-gap: ${legacyTheme.spacing.s48};

  justify-items: center;
  grid-row: 1 / -1;
  grid-column: 1 / -1;
`;

const StyledSlider = styled(Slider)`
  .slick-prev {
    z-index: 1;
    height: auto;

    ::before {
      content: '';
      color: black;
    }
  }

  .slick-next {
    height: auto;

    ::before {
      content: '';
      color: black;
    }
  }

  .slick-dots {
    bottom: -40px;

    li {
      margin: 0;
    }

    li button::before {
      font-size: 8px;
      color: ${theme.colors.neutral[600]};
    }

    li.slick-active button::before {
      color: white;
    }
  }
`;

type StepCardProps = {
  title: string;
  body: string;
  icon: string;
};

const StepCard = ({ title, body, icon }: StepCardProps) => {
  const { isMobile } = useResponsiveVariant();

  return (
    <CardContainer>
      {isMobile && <Icon alt="icon" src={icon} />}

      <Title align="center" variant="mono1">
        {title}
      </Title>

      <Typography align="center">{body}</Typography>
    </CardContainer>
  );
};

export const HowProseWorksSection = () => {
  const [showPrevArrow, setShowPrevArrow] = useState(false);
  const [showNextArrow, setShowNextArrow] = useState(true);

  const isTablet = useMedia(`(min-width: ${breakpoints.sm}px)`);
  const { headline, sectionContent } = content;

  return (
    <SectionContainer>
      <StyledTextSectionTitle align="center" variant="h1r-mono">
        {headline}
      </StyledTextSectionTitle>
      {isTablet ? (
        <StepContainer>
          <IconsContainer>
            <LineContainer>
              <Line />
            </LineContainer>
            <IconContainer>
              <Icon alt="product-icon-1" src={Icon1} />
              <Icon alt="product-icon-2" src={Icon2} />
              <Icon alt="product-icon-3" src={Icon3} />
            </IconContainer>
          </IconsContainer>
          <StepCards>
            {sectionContent.map(({ title, body, icon }) => (
              <StepCard key={title} body={body} icon={icon} title={title} />
            ))}
          </StepCards>
        </StepContainer>
      ) : (
        <StyledSlider
          beforeChange={(_current, next) => {
            // display previous arrow rule
            setShowPrevArrow(next > 0);

            // display next arrow rule
            setShowNextArrow(next !== 2);
          }}
          dots
          fade
          focusOnSelect
          infinite={false}
          nextArrow={showNextArrow ? <SliderArrow direction="next" /> : undefined}
          prevArrow={showPrevArrow ? <SliderArrow direction="prev" /> : undefined}
          slidesToScroll={1}
          speed={500}
        >
          {sectionContent.map(({ title, body, icon }) => (
            <StepCard key={title} body={body} icon={icon} title={title} />
          ))}
        </StyledSlider>
      )}
    </SectionContainer>
  );
};
